import type { Creative, CreativeAsset } from '@/api/types';
import { CHANNEL_FORMATS } from '@/screens/creative-config';

/** The asset fields a library preview reads. A stored `CreativeAsset` satisfies it. */
export type LibraryAsset = Pick<
  CreativeAsset,
  | 'asset_id'
  | 'kind'
  | 'width'
  | 'height'
  | 'duration_ms'
  | 'upload_status'
  | 'placement_tags'
  | 'view_url'
  | 'animation'
> & { byte_size?: CreativeAsset['byte_size'] };

/** The creative fields a library preview reads. A stored `Creative` satisfies it. */
export type LibraryCreative = Pick<Creative, 'id' | 'name' | 'destination_url'> & {
  assets: readonly LibraryAsset[];
};

export type LibraryChannel = 'meta' | 'ctv' | 'dsp';

/** A presentation-only preview of one stored asset. It holds no `File`. */
export interface LibraryPreviewAsset {
  type: 'image' | 'video';
  mediaKind: 'image' | 'video';
  name: string;
  url: string;
  width: number | null;
  height: number | null;
  w: number | null;
  h: number | null;
  durationMs: number | null;
  duration: number | null;
  previewOnly: true;
  fromLibrary: { id: string; name: string };
}

export interface LibraryBannerSize {
  name: string;
  w: number;
  h: number;
}

export interface LibraryBanner extends Omit<LibraryPreviewAsset, 'w' | 'h' | 'width' | 'height'> {
  id: string;
  w: number;
  h: number;
  width: number;
  height: number;
  animation: NonNullable<CreativeAsset['animation']> | null;
  byteSize: number;
  size: LibraryBannerSize;
}

/** Meta previews keyed by placement format key (`sq`, `fs`, `story`). */
export type MetaLibraryAssets = Record<string, LibraryPreviewAsset>;

export interface CtvLibraryAssets {
  destUrl: string;
  asset: LibraryPreviewAsset | null;
}

export interface DspLibraryAssets {
  destUrl: string;
  banners: LibraryBanner[];
}

export type LibraryAssets = MetaLibraryAssets | CtvLibraryAssets | DspLibraryAssets;

function previewAsset(
  asset: LibraryAsset | undefined,
  creative: LibraryCreative,
): LibraryPreviewAsset | null {
  if (!asset?.view_url || asset.upload_status !== 'uploaded') return null;
  const mediaKind = asset.kind === 'video' ? 'video' : 'image';
  return {
    type: mediaKind,
    mediaKind,
    name: asset.asset_id,
    url: asset.view_url,
    width: asset.width,
    height: asset.height,
    w: asset.width,
    h: asset.height,
    durationMs: asset.duration_ms,
    duration: asset.duration_ms ? asset.duration_ms / 1000 : null,
    previewOnly: true,
    fromLibrary: { id: creative.id, name: creative.name },
  };
}

// Build presentation-only previews for an existing library reference from
// its actual R2 objects. These records intentionally have no `File`;
// selecting a library creative references its id and never duplicates bytes.
export function libraryToAssets(
  creative: LibraryCreative | null | undefined,
  production: unknown,
  channel: 'meta',
): MetaLibraryAssets;
export function libraryToAssets(
  creative: LibraryCreative | null | undefined,
  production: unknown,
  channel: 'ctv',
): CtvLibraryAssets;
export function libraryToAssets(
  creative: LibraryCreative | null | undefined,
  production: unknown,
  channel: 'dsp',
): DspLibraryAssets;
export function libraryToAssets(
  creative: LibraryCreative | null | undefined,
  production: unknown,
  channel: LibraryChannel,
): LibraryAssets;
export function libraryToAssets(
  creative: LibraryCreative | null | undefined,
  _production: unknown,
  channel: LibraryChannel,
): LibraryAssets {
  const assets = creative?.assets ?? [];
  if (channel === 'meta') {
    const output: MetaLibraryAssets = {};
    if (!creative) return output;
    CHANNEL_FORMATS.meta.formats.forEach((format) => {
      const asset = assets.find((candidate) =>
        candidate.placement_tags?.includes(`meta_${format.key}`));
      const preview = previewAsset(asset, creative);
      if (preview) output[format.key] = preview;
    });
    return output;
  }

  if (channel === 'ctv') {
    const asset = assets.find((candidate) =>
      candidate.placement_tags?.includes('ctv_16x9'));
    return {
      destUrl: creative?.destination_url || '',
      asset: creative ? previewAsset(asset, creative) : null,
    };
  }

  const sizes: readonly LibraryBannerSize[] = CHANNEL_FORMATS.dsp.dsp.sizes;
  const banners = creative
    ? assets.flatMap((asset) =>
      (asset.placement_tags || [])
        .filter((tag) => tag.startsWith('dsp_'))
        .map((tag): LibraryBanner | null => {
          const preview = previewAsset(asset, creative);
          if (!preview) return null;
          const dimensions = tag.match(/dsp_(\d{2,4})x(\d{2,4})/);
          const width = asset.width ?? (dimensions ? Number(dimensions[1]) : null);
          const height = asset.height ?? (dimensions ? Number(dimensions[2]) : null);
          if (!width || !height) return null;
          return {
            ...preview,
            id: `b-lib-${creative.id}-${asset.asset_id}-${tag}`,
            w: width,
            h: height,
            width,
            height,
            // Read from the stored bytes at attach; the wizard's tiles and review
            // step warn on it exactly as they do for a freshly picked file.
            animation: asset.animation ?? null,
            byteSize: Number(asset.byte_size),
            size: sizes.find((size) => size.w === width && size.h === height)
              || { name: `${width}×${height}`, w: width, h: height },
          };
        })
        .filter((banner): banner is LibraryBanner => banner !== null))
    : [];
  return { destUrl: creative?.destination_url || '', banners };
}
